import { type ReactNode, useEffect, useRef, useState } from 'react'

import {
  A2AssistantLauncherContext,
  type A2AssistantLauncher,
} from 'src/components/A2Workspace/A2AssistantLauncherContext'
import { A2SearchContext } from 'src/components/A2Workspace/A2SearchContext'
import { A2CaseNavigation } from 'src/components/A2Workspace/A2CaseNavigation'
import { openSupportHome } from 'src/components/ApplicationAssistant/useAssistantSideEffects'
import { Button, Icon, MessagesCircleIcon } from 'src/design-system/ui'
import ProfessionalDashboardLayout from 'src/layouts/ProfessionalDashboardLayout/ProfessionalDashboardLayout'
import { useLocation } from 'src/platform/router'

/** A2 shares Professional chrome while retaining its application support entry. */
const Agent2DashboardLayout = ({ children }: { children: ReactNode }) => {
  const isApplication = !/^\/a2(?:-evie)?\/?$/.test(useLocation().pathname)
  const [pms, setPms] = useState<string | undefined>(undefined)
  const [assistantLauncher, setAssistantLauncher] =
    useState<A2AssistantLauncher | null>(null)
  const launcherButton = useRef<HTMLButtonElement>(null)
  const restoreAssistantFocus = useRef(false)
  useEffect(() => {
    if (
      !assistantLauncher ||
      assistantLauncher.visible ||
      !restoreAssistantFocus.current
    )
      return
    // The launcher hides while the drawer is open, so restore it after the
    // sheet's own focus cleanup and the shell's visibility update settle.
    const frame = requestAnimationFrame(() => {
      launcherButton.current?.focus()
      restoreAssistantFocus.current = false
    })
    return () => cancelAnimationFrame(frame)
  }, [assistantLauncher])
  return (
    <A2SearchContext.Provider value={{ pms, setPms }}>
      <A2AssistantLauncherContext.Provider value={setAssistantLauncher}>
        <ProfessionalDashboardLayout
          layout={isApplication ? 'workspace' : 'page'}
          navigation={
            isApplication
              ? (content) => <A2CaseNavigation>{content}</A2CaseNavigation>
              : undefined
          }
          actions={
            <span hidden={assistantLauncher?.visible}>
              <Button
                ref={launcherButton}
                variant="secondary"
                size="compact"
                leadingIcon={<Icon icon={MessagesCircleIcon} size="sm" />}
                onPress={() => {
                  if (assistantLauncher) {
                    restoreAssistantFocus.current = true
                    assistantLauncher.open()
                  } else openSupportHome()
                }}
              >
                {assistantLauncher ? 'Assistant' : 'Support'}
              </Button>
            </span>
          }
        >
          {children}
        </ProfessionalDashboardLayout>
      </A2AssistantLauncherContext.Provider>
    </A2SearchContext.Provider>
  )
}
export default Agent2DashboardLayout
